import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Card,
    CardContent,
    Chip,
    Divider,
    Stack,
    Breadcrumbs,
    Link,
    Avatar,
    AvatarGroup,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { alpha, useTheme } from '@mui/material/styles';

const defaultColumns = ['todo', 'in-progress', 'done'];

const TaskBoard = () => {
    const { id: projectId } = useParams();
    const navigate = useNavigate();
    const token = useSelector((state) => state.auth.token);
    const user = useSelector((state) => state.auth.user);
    const [tasks, setTasks] = useState([]);
    const [columns, setColumns] = useState(defaultColumns);
    const [projectName, setProjectName] = useState('');
    const [newColumn, setNewColumn] = useState('');
    const [selectedTask, setSelectedTask] = useState(null);
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [currentColumn, setCurrentColumn] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [taskDialogOpen, setTaskDialogOpen] = useState(false);
    const [taskForm, setTaskForm] = useState({
        title: '',
        assignees: [],
        dueDate: '',
        description: '',
        comment: '',
        status: 'todo',
    });
    const theme = useTheme();

    const groupedTasks = useMemo(() => {
        return columns.reduce((acc, columnKey) => {
            acc[columnKey] = tasks.filter((task) => task.status === columnKey);
            return acc;
        }, {});
    }, [columns, tasks]);

    const fetchTasks = async () => {
        const res = await axios.get(`/api/tasks/project/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setTasks(res.data);
    };

    const fetchProject = async () => {
        const res = await axios.get(`/api/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setColumns(res.data.project.taskColumns || defaultColumns);
        setProjectName(res.data.project.projectName || 'Project');
    };

    useEffect(() => {
        fetchTasks();
        fetchProject();
    }, [projectId]);

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/team', { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => {
                const activeMembers = res.data.filter(
                    (member) => member.status === 'active' && member.userId && typeof member.userId === 'object'
                );
                setTeamMembers(activeMembers);
            })
            .catch(() => setTeamMembers([]));
    }, [token]);

    const addColumn = async () => {
        const col = newColumn.trim();
        if (!col) return;
        const updated = [...columns, col];
        await axios.put(`/api/projects/${projectId}/task-columns`, { columns: updated }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setColumns(updated);
        setNewColumn('');
    };

    const openColumnMenu = (event, col) => {
        setColumnMenuAnchor(event.currentTarget);
        setCurrentColumn(col);
    };

    const closeColumnMenu = () => {
        setColumnMenuAnchor(null);
        setCurrentColumn(null);
    };

    const renameColumn = async () => {
        const name = window.prompt('New column name', currentColumn);
        if (!name) return closeColumnMenu();
        const updated = columns.map((c) => (c === currentColumn ? name : c));
        await axios.put(`/api/projects/${projectId}/task-columns`, { columns: updated }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setColumns(updated);
        closeColumnMenu();
    };

    const deleteColumn = async () => {
        if (!window.confirm('Delete column?')) return closeColumnMenu();
        const updated = columns.filter((c) => c !== currentColumn);
        await axios.put(`/api/projects/${projectId}/task-columns`, { columns: updated }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setColumns(updated);
        closeColumnMenu();
    };

    const openTaskDialog = ({ status = 'todo', title = '' } = {}) => {
        setTaskForm({
            title,
            assignees: [],
            dueDate: '',
            description: '',
            comment: '',
            status,
        });
        setTaskDialogOpen(true);
    };

    const closeTaskDialog = () => {
        setTaskDialogOpen(false);
    };

    const handleTaskFormChange = (field) => (event) => {
        const value = event?.target ? event.target.value : event;
        setTaskForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleCreateTask = async () => {
        if (!taskForm.title.trim()) return;
        const payload = {
            projectId,
            title: taskForm.title.trim(),
            description: taskForm.description.trim() || undefined,
            status: taskForm.status,
            assignedTo: taskForm.assignees,
            dueDate: taskForm.dueDate || undefined,
        };
        const res = await axios.post('/api/tasks', payload, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (taskForm.comment.trim()) {
            await axios.post(
                `/api/tasks/${res.data._id}/comment`,
                { comment: taskForm.comment.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        }
        setTaskDialogOpen(false);
        fetchTasks();
    };

    const handleDragEnd = async (result) => {
        const { destination, source, draggableId, type } = result;
        if (!destination) return;

        if (type === 'column') {
            const reordered = Array.from(columns);
            const [removed] = reordered.splice(source.index, 1);
            reordered.splice(destination.index, 0, removed);
            await axios.put(`/api/projects/${projectId}/task-columns`, { columns: reordered }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setColumns(reordered);
            return;
        }

        const status = destination.droppableId;
        await axios.put(
            `/api/tasks/${draggableId}`,
            { status },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchTasks();
    };

    const openDetails = (task) => setSelectedTask(task);
    const closeDetails = () => setSelectedTask(null);

    const avatarUrl = (member) =>
        member?.avatarUrl ? `/api/users/avatars/${member._id}/${member.avatarUrl.split('/').pop()}` : '';

    const getInitials = (firstName = '', lastName = '') =>
        `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

    const formatDueDate = (value) => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
    };

    const getAssignees = (assignees) => {
        if (Array.isArray(assignees)) return assignees;
        return assignees ? [assignees] : [];
    };

    const collaboratorList = useMemo(() => {
        const selectedAssignees = taskForm.assignees
            .map((id) => teamMembers.find((member) => member.userId?._id === id)?.userId)
            .filter(Boolean);
        const base = user ? [user] : [];
        const merged = [...base, ...selectedAssignees];
        const seen = new Set();
        return merged.filter((member) => {
            if (!member?._id || seen.has(member._id)) return false;
            seen.add(member._id);
            return true;
        });
    }, [taskForm.assignees, teamMembers, user]);

    return (
        <Box
            sx={{
                py: 3,
                px: { xs: 2, md: 4 },
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                minHeight: '100%',
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Stack spacing={1} sx={{ mb: 1 }}>
                        <Button
                            variant="text"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(`/dashboard/projects/${projectId}`)}
                            sx={{ alignSelf: 'flex-start', px: 0 }}
                        >
                            Back to project
                        </Button>
                        <Breadcrumbs aria-label="breadcrumb">
                            <Link component={RouterLink} underline="hover" color="inherit" to="/dashboard/projects">
                                Projects
                            </Link>
                            <Link
                                component={RouterLink}
                                underline="hover"
                                color="inherit"
                                to={`/dashboard/projects/${projectId}`}
                            >
                                {projectName || 'Project'}
                            </Link>
                            <Typography color="text.primary">Task Board</Typography>
                        </Breadcrumbs>
                    </Stack>
                    <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
                        Task Board
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Organize your project workstreams in a familiar board layout inspired by Asana.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openTaskDialog({ status: 'todo' })}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    Add Task
                </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
                <TextField
                    value={newColumn}
                    onChange={(e) => setNewColumn(e.target.value)}
                    placeholder="New column name"
                    size="small"
                    sx={{ maxWidth: 260 }}
                />
                <Button variant="outlined" onClick={addColumn} startIcon={<AddIcon />}>
                    Add Column
                </Button>
            </Stack>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="all-columns" direction="horizontal" type="column">
                    {(provided) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 2,
                                overflowX: 'auto',
                                pb: 2,
                            }}
                        >
                            {columns.map((col, index) => (
                                <Draggable draggableId={col} index={index} key={col}>
                                    {(colProvided) => (
                                        <Box
                                            ref={colProvided.innerRef}
                                            {...colProvided.draggableProps}
                                            sx={{
                                                width: { xs: '100%', sm: 320 },
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: 3,
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    height: '100%',
                                                    boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.08)',
                                                }}
                                            >
                                                <Box
                                                    {...colProvided.dragHandleProps}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        px: 2,
                                                        py: 1.5,
                                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                                    }}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                textTransform: 'capitalize',
                                                                fontWeight: 700,
                                                                letterSpacing: 0.4,
                                                            }}
                                                        >
                                                            {col.replace('-', ' ')}
                                                        </Typography>
                                                        <Chip
                                                            label={groupedTasks[col]?.length || 0}
                                                            size="small"
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                    </Stack>
                                                    <IconButton size="small" onClick={(e) => openColumnMenu(e, col)}>
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <Droppable droppableId={col} type="task">
                                                    {(taskProvided) => (
                                                        <CardContent
                                                            ref={taskProvided.innerRef}
                                                            {...taskProvided.droppableProps}
                                                            sx={{
                                                                flexGrow: 1,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1.5,
                                                                bgcolor: alpha(theme.palette.background.default, 0.65),
                                                            }}
                                                        >
                                                            <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
                                                                {groupedTasks[col]?.map((t, index) => (
                                                                    <Draggable draggableId={t._id} index={index} key={t._id}>
                                                                        {(dragProvided) => (
                                                                            <Card
                                                                                ref={dragProvided.innerRef}
                                                                                {...dragProvided.draggableProps}
                                                                                {...dragProvided.dragHandleProps}
                                                                                variant="outlined"
                                                                                onClick={() => openDetails(t)}
                                                                                sx={{
                                                                                    cursor: 'pointer',
                                                                                    borderRadius: 2,
                                                                                    boxShadow: '0px 4px 16px rgba(15, 23, 42, 0.08)',
                                                                                    transition: 'all 0.2s ease',
                                                                                    '&:hover': {
                                                                                        boxShadow: '0px 10px 24px rgba(15, 23, 42, 0.12)',
                                                                                        transform: 'translateY(-2px)',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <CardContent sx={{ p: 2 }}>
                                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                                        {t.title}
                                                                                    </Typography>
                                                                                    {t.dueDate && (
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                            Due {formatDueDate(t.dueDate)}
                                                                                        </Typography>
                                                                                    )}
                                                                                    {t.description && (
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            color="text.secondary"
                                                                                            sx={{
                                                                                                mt: 0.75,
                                                                                                display: '-webkit-box',
                                                                                                overflow: 'hidden',
                                                                                                WebkitBoxOrient: 'vertical',
                                                                                                WebkitLineClamp: 3,
                                                                                            }}
                                                                                        >
                                                                                            {t.description}
                                                                                        </Typography>
                                                                                    )}
                                                                                    {getAssignees(t.assignedTo).length > 0 && (
                                                                                        <AvatarGroup
                                                                                            max={4}
                                                                                            sx={{
                                                                                                mt: 1,
                                                                                                '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12 },
                                                                                            }}
                                                                                        >
                                                                                            {getAssignees(t.assignedTo).map((member) => (
                                                                                                <Avatar
                                                                                                    key={member._id || member}
                                                                                                    src={avatarUrl(member)}
                                                                                                    alt={`${member.firstName || ''} ${member.lastName || ''}`}
                                                                                                >
                                                                                                    {getInitials(member.firstName, member.lastName)}
                                                                                                </Avatar>
                                                                                            ))}
                                                                                        </AvatarGroup>
                                                                                    )}
                                                                                </CardContent>
                                                                            </Card>
                                                                        )}
                                                                    </Draggable>
                                                                ))}
                                                                {taskProvided.placeholder}
                                                            </Stack>
                                                            <Divider flexItem sx={{ my: 0.5 }} />
                                                            <Button
                                                                startIcon={<AddIcon />}
                                                                onClick={() => openTaskDialog({ status: col })}
                                                                sx={{
                                                                    justifyContent: 'flex-start',
                                                                    color: theme.palette.primary.main,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                Add task
                                                            </Button>
                                                        </CardContent>
                                                    )}
                                                </Droppable>
                                            </Card>
                                        </Box>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>

            <Menu anchorEl={columnMenuAnchor} open={Boolean(columnMenuAnchor)} onClose={closeColumnMenu}>
                <MenuItem onClick={renameColumn}>Rename</MenuItem>
                <MenuItem onClick={deleteColumn}>Delete</MenuItem>
            </Menu>

            <Dialog open={taskDialogOpen} onClose={closeTaskDialog} fullWidth maxWidth="sm">
                <DialogTitle>New Task</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label="Task name"
                        value={taskForm.title}
                        onChange={handleTaskFormChange('title')}
                        required
                        autoFocus
                    />
                    <FormControl>
                        <InputLabel id="task-assignees-label">Assignees</InputLabel>
                        <Select
                            labelId="task-assignees-label"
                            multiple
                            value={taskForm.assignees}
                            onChange={handleTaskFormChange('assignees')}
                            label="Assignees"
                            renderValue={(selected) =>
                                selected
                                    .map((id) => {
                                        const member = teamMembers.find((item) => item.userId?._id === id)?.userId;
                                        return member ? `${member.firstName || ''} ${member.lastName || ''}`.trim() : '';
                                    })
                                    .filter(Boolean)
                                    .join(', ')
                            }
                        >
                            {teamMembers.length === 0 && (
                                <MenuItem disabled>No team members available</MenuItem>
                            )}
                            {teamMembers.map((member) => (
                                <MenuItem key={member.userId._id} value={member.userId._id}>
                                    {member.userId.firstName} {member.userId.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Due date"
                        type="date"
                        value={taskForm.dueDate}
                        onChange={handleTaskFormChange('dueDate')}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Description"
                        value={taskForm.description}
                        onChange={handleTaskFormChange('description')}
                        multiline
                        minRows={3}
                    />
                    <TextField
                        label="Comments"
                        value={taskForm.comment}
                        onChange={handleTaskFormChange('comment')}
                        multiline
                        minRows={2}
                    />
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Collaborators
                        </Typography>
                        {collaboratorList.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Add assignees to show collaborators.
                            </Typography>
                        ) : (
                            <AvatarGroup max={6} sx={{ '& .MuiAvatar-root': { width: 32, height: 32 } }}>
                                {collaboratorList.map((member) => (
                                    <Avatar
                                        key={member._id}
                                        src={avatarUrl(member)}
                                        alt={`${member.firstName || ''} ${member.lastName || ''}`}
                                    >
                                        {getInitials(member.firstName, member.lastName)}
                                    </Avatar>
                                ))}
                            </AvatarGroup>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeTaskDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateTask} disabled={!taskForm.title.trim()}>
                        Create Task
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Task detail dialog */}
            <Dialog open={!!selectedTask} onClose={closeDetails} fullWidth>
                {selectedTask && (
                    <>
                        <DialogTitle>{selectedTask.title}</DialogTitle>
                        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Description
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedTask.description || 'No description'}
                                </Typography>
                            </Box>
                            {selectedTask.dueDate && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Due date
                                    </Typography>
                                    <Typography variant="body2">{formatDueDate(selectedTask.dueDate)}</Typography>
                                </Box>
                            )}
                            {getAssignees(selectedTask.assignedTo).length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Assignees
                                    </Typography>
                                    <AvatarGroup max={6} sx={{ '& .MuiAvatar-root': { width: 32, height: 32 } }}>
                                        {getAssignees(selectedTask.assignedTo).map((member) => (
                                            <Avatar
                                                key={member._id || member}
                                                src={avatarUrl(member)}
                                                alt={`${member.firstName || ''} ${member.lastName || ''}`}
                                            >
                                                {getInitials(member.firstName, member.lastName)}
                                            </Avatar>
                                        ))}
                                    </AvatarGroup>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDetails}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default TaskBoard;
